/**
 * Performance Monitor Utility
 * 
 * Tracks Core Web Vitals and custom performance metrics
 */

const isProduction = typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';
const perfWarn = (...args: unknown[]): void => {
  if (isProduction) return;
  const warn: (...params: unknown[]) => void = console.warn;
  warn(...args);
};

interface PerformanceMetrics {
  lcp: number | null;
  fid: number | null;
  cls: number | null;
  ttfb: number | null;
  fcp: number | null;
  customMetrics: Record<string, number>;
}

class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private readonly metrics: PerformanceMetrics = {
    lcp: null,
    fid: null,
    cls: null,
    ttfb: null,
    fcp: null,
    customMetrics: {},
  };
  
  private observer: PerformanceObserver | null = null;
  private fidObserver: PerformanceObserver | null = null;
  private clsObserver: PerformanceObserver | null = null;
  private isMonitoring = false;
  private clsValue = 0;
  private clsEntries: PerformanceEntry[] = [];

  private constructor() {
    // Singleton pattern
  }

  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Start monitoring Core Web Vitals and other performance metrics
   */
  public startMonitoring(reportCallback?: (metrics: PerformanceMetrics) => void): void {
    if (this.isMonitoring || !this.isPerformanceSupported()) {
      return;
    }
    
    this.isMonitoring = true;
    
    // Measure Time to First Byte (TTFB)
    this.measureTTFB();
    
    // Monitor Largest Contentful Paint (LCP)
    this.monitorLCP();
    
    // Monitor First Input Delay (FID)
    this.monitorFID();
    
    // Monitor Cumulative Layout Shift (CLS)
    this.monitorCLS();

    // Monitor First Contentful Paint (FCP)
    this.monitorFCP();
    
    // Set up periodic reporting if callback provided
    if (reportCallback) {
      // Report initially after 5 seconds to capture early metrics
      setTimeout(() => reportCallback(this.getMetrics()), 5000);
      
      // Then report every 15 seconds
      setInterval(() => reportCallback(this.getMetrics()), 15000);
      
      // Also report on page unload
      window.addEventListener('unload', () => reportCallback(this.getMetrics()));
    }
  }
  
  /**
   * Get current performance metrics
   */
  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }
  
  /**
   * Start measuring a custom performance metric
   */
  public startMeasure(name: string): void {
    if (!this.isPerformanceSupported()) return;
    
    performance.mark(`${name}-start`);
  }
  
  /**
   * End measuring a custom performance metric and record it
   */
  /** windsurf generated code start */
  public endMeasure(name: string): number | null {
    if (!this.isPerformanceSupported()) return null;
    
    try {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);
      
      const entries = performance.getEntriesByName(name);
      if (entries.length > 0) {
        const duration = entries[0].duration;
        this.metrics.customMetrics[name] = duration;
        return duration;
      }
    } catch (error) {
      perfWarn(`Failed to measure ${name}:`, error);
    }
    
    return null;
  }
  /** windsurf generated code end */
  
  /**
   * Log a function's execution time
   */
  public measureFunction<T>(name: string, fn: () => T): T {
    this.startMeasure(name);
    const result = fn();
    this.endMeasure(name);
    return result;
  }
  
  /**
   * Create a wrapped version of a function that measures its execution time
   */
  /** windsurf generated code start */
  public createMeasuredFunction<T>(name: string, fn: (...args: any[]) => T): (...args: any[]) => T {
    return (...args: any[]) => {
      this.startMeasure(name);
      try {
        const result = fn(...args);
        
        // Handle promises correctly
        if (result instanceof Promise) {
          return result.finally(() => {
            this.endMeasure(name);
          }) as T;
        } else {
          this.endMeasure(name);
          return result;
        }
      } catch (error) {
        // Make sure to end measurement even if an error occurs
        this.endMeasure(name);
        throw error; // Re-throw the error to maintain original behavior
      }
    };
  }
  /** windsurf generated code end */
  
  /**
   * Stop monitoring and clean up resources
   */
  public stopMonitoring(): void {
    if (!this.isMonitoring) return;
    
    this.isMonitoring = false;
    
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    if (this.fidObserver) {
      this.fidObserver.disconnect();
      this.fidObserver = null;
    }
    
    if (this.clsObserver) {
      this.clsObserver.disconnect();
      this.clsObserver = null;
    }
  }
  
  /** windsurf generated code start */
private isPerformanceSupported(): boolean {
    return typeof window !== 'undefined' && 
           typeof performance !== 'undefined' && 
           typeof PerformanceObserver !== 'undefined';
  }
/** windsurf generated code end */
  
  private measureTTFB(): void {
    if (!performance?.getEntriesByType) return;
    
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      this.metrics.ttfb = navEntry.responseStart;
    }
  }
  
  private monitorLCP(): void {
    try {
      this.observer = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        if (entries?.length > 0) {
          const lastEntry = entries[entries.length - 1];
          this.metrics.lcp = lastEntry.startTime;
        }
      });
      
      this.observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      perfWarn('LCP monitoring not supported', e);
    }
  }
  
  private monitorFID(): void {
    try {
      this.fidObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        if (entries?.length > 0) {
          const firstInput = entries[0] as FirstInputEntry;
          this.metrics.fid = firstInput.processingStart - firstInput.startTime;
        }
      });
      
      this.fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      perfWarn('FID monitoring not supported', e);
    }
  }
  
  private monitorCLS(): void {
    try {
      let sessionValue = 0;
      let sessionEntries: PerformanceEntry[] = [];
      let lastSessionTime = 0;
      const windowTime = 5000; // 5-second window
      
      this.clsObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        if (entries?.length > 0) {
          const result = this.processLayoutShifts(entries, sessionValue, sessionEntries, lastSessionTime, windowTime);
          // Update the variables with returned values
          if (result) {
            sessionValue = result.sessionValue;
            sessionEntries = result.sessionEntries;
            lastSessionTime = result.lastSessionTime;
          }
        }
      });
      
      this.clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      perfWarn('CLS monitoring not supported', e);
    }
  }
  
  private processLayoutShifts(entries: PerformanceEntry[], sessionValue: number, sessionEntries: PerformanceEntry[], lastSessionTime: number, windowTime: number): { sessionValue: number, sessionEntries: PerformanceEntry[], lastSessionTime: number } | null {
    for (const entry of entries) {
      const layoutShift = entry as unknown as LayoutShift;
      if (!layoutShift.hadRecentInput) {
        const currentTime = entry.startTime;
        
        // Check if we need to start a new session
        const sessionData = this.processLayoutShiftSession(
          layoutShift,
          entry,
          sessionValue,
          sessionEntries,
          lastSessionTime,
          currentTime,
          windowTime
        );
        
        // Update with values from the processed session
        sessionValue = sessionData.sessionValue;
        sessionEntries = sessionData.sessionEntries;
        lastSessionTime = sessionData.lastSessionTime;
      }
    }
    
    return { sessionValue, sessionEntries, lastSessionTime };
  }

  /**
   * Process a layout shift session and determine if a new session should begin
   */
  private processLayoutShiftSession(
    layoutShift: LayoutShift,
    entry: PerformanceEntry,
    sessionValue: number,
    sessionEntries: PerformanceEntry[],
    lastSessionTime: number,
    currentTime: number,
    windowTime: number
  ): { sessionValue: number, sessionEntries: PerformanceEntry[], lastSessionTime: number } {
    // If this is a new session (gap of > X ms), finalize the previous session
    if (lastSessionTime && (currentTime - lastSessionTime > windowTime)) {
      // Update metrics if this session has a higher CLS
      this.updateCLSMetricsIfHigher(sessionValue, sessionEntries);
      
      // Start a new session
      sessionValue = 0;
      sessionEntries = [];
    }
    
    // Add the entry to the current session
    sessionValue += layoutShift.value;
    sessionEntries.push(entry);
    lastSessionTime = currentTime;
    
    // Update metrics with the current session value
    this.updateCLSMetricsIfHigher(sessionValue, sessionEntries);
    
    return { sessionValue, sessionEntries, lastSessionTime };
  }
  
  private updateCLSMetricsIfHigher(sessionValue: number, sessionEntries: PerformanceEntry[]): void {
    if (sessionValue > this.clsValue) {
      this.clsValue = sessionValue;
      this.clsEntries = sessionEntries;
      this.metrics.cls = this.clsValue;
    }
  }
  
  private monitorFCP(): void {
    try {
      const fcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        if (entries?.length > 0) {
          const firstFCP = entries[0];
          this.metrics.fcp = firstFCP.startTime;
          
          // FCP only fires once, so we can disconnect this observer
          fcpObserver.disconnect();
        }
      });
      
      fcpObserver.observe({ type: 'paint', buffered: true });
    } catch (e) {
      perfWarn('FCP monitoring not supported', e);
    }
  }
  
  /*
   * Performance metric submission is temporarily disabled while CWV reports
   * are reviewed in the browser console.
   *
   * public sendMetricsToAnalytics(endpoint: string): Promise<Response> {
   *   const metrics = this.getMetrics();
   *
   *   return fetch(endpoint, {
   *     method: 'POST',
   *     headers: { 'Content-Type': 'application/json' },
   *     body: JSON.stringify({
   *       metrics,
   *       url: window.location.href,
   *       timestamp: Date.now(),
   *       userAgent: navigator.userAgent
   *     }),
   *     keepalive: true
   *   });
   * }
   */

  /**
   * Generate a performance report suitable for logging
   */
  public generateReport(): {
    lcp: string;
    fid: string;
    cls: string;
    ttfb: string;
    fcp: string;
    customMetrics: string;
  } {
    const metrics = this.getMetrics();
    
    const formatMs = (ms: number | null) => ms !== null ? `${Math.round(ms)}ms` : 'Not measured';
    const performanceReport ={
        lcp: `${formatMs(metrics.lcp)} ${this.getRating(metrics.lcp, 2500, 4000)}`,
        fid: `${formatMs(metrics.fid)} ${this.getRating(metrics.fid, 100, 300)}`,
        cls: `${metrics.cls !== null ? metrics.cls.toFixed(3) : 'Not measured'} ${this.getRatingCLS(metrics.cls, 0.1, 0.25)}`,
        ttfb: `${formatMs(metrics.ttfb)}`,
        fcp: `${formatMs(metrics.fcp)}`,
        customMetrics: Object.entries(metrics.customMetrics)
          .map(([name, value]) => `- ${name}: ${formatMs(value)}`)
          .join('\n')
    }
    
    return performanceReport;
  }
  
  private getRating(value: number | null, good: number, poor: number): string {
    if (value === null) return '';
    if (value <= good) return '(Good)';
    if (value <= poor) return '(Needs Improvement)';
    return '(Poor)';
  }
  
  private getRatingCLS(value: number | null, good: number, poor: number): string {
    // For CLS, lower values are better
    if (value === null) return '';
    if (value <= good) return '(Good)';
    if (value <= poor) return '(Needs Improvement)';
    return '(Poor: ' + value.toFixed(3) + ')';
  }
}

// Type definition for Layout Shift entries
// Type definitions for Web Performance API elements not fully covered by lib.dom.d.ts
interface LayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

interface FirstInputEntry extends PerformanceEntry {
  processingStart: number;
  startTime: number;
}

export default PerformanceMonitor;
